import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

function AboutHistory() {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timelineProgress, setTimelineProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.innerWidth <= 767;
  });
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.innerWidth >= 992;
  });
  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const timelineRef = useRef(null);

  const timelineDragRef = useRef({
    isDragging: false,
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
  });

  const [isTimelineDragging, setIsTimelineDragging] = useState(false);

  const API_BASE_URL = (
    process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"
  ).replace(/\/+$/, "");

  /* =========================================================
     GÖRSEL URL
  ========================================================= */

  const getImageUrl = useCallback(
    (imageValue) => {
      if (!imageValue) {
        return "";
      }

      let imagePath = "";

      if (typeof imageValue === "string") {
        imagePath = imageValue;
      } else if (typeof imageValue === "object" && imageValue !== null) {
        imagePath = imageValue.url || imageValue.path || imageValue.src || "";
      }

      if (!imagePath) {
        return "";
      }

      const trimmedPath = String(imagePath).trim();

      if (
        trimmedPath.startsWith("http://") ||
        trimmedPath.startsWith("https://") ||
        trimmedPath.startsWith("data:") ||
        trimmedPath.startsWith("blob:")
      ) {
        return trimmedPath;
      }

      const cleanPath = trimmedPath.replace(/^\/+/, "");

      return `${API_BASE_URL}/cdn/${cleanPath}`;
    },
    [API_BASE_URL],
  );

  /* =========================================================
     API
  ========================================================= */

  useEffect(() => {
    const controller = new AbortController();

    const fetchHistory = async () => {
      try {
        setLoading(true);

        const searchParams = new URLSearchParams(window.location.search);

        const currentLang = searchParams.get("lang") || "tr";

        const response = await fetch(
          `${API_BASE_URL}/api/about/history?lang=${encodeURIComponent(
            currentLang,
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`Tarihçe alınamadı: ${response.status}`);
        }

        const responseData = await response.json();

        let list = [];

        if (Array.isArray(responseData)) {
          list = responseData;
        } else if (Array.isArray(responseData?.data)) {
          list = responseData.data;
        } else if (Array.isArray(responseData?.history)) {
          list = responseData.history;
        }

        setHistoryData(list);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("ABOUT_HISTORY_API_ERROR:", error);

          setHistoryData([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchHistory();

    return () => {
      controller.abort();
    };
  }, [API_BASE_URL]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 767);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  /* =========================================================
     NORMALIZE + SIRALA
  ========================================================= */

  const timelineItems = useMemo(() => {
    const getYear = (date) => {
      if (!date) {
        return 9999;
      }

      const match = String(date).match(/\d{4}/);

      return match ? Number(match[0]) : 9999;
    };

    return historyData
      .map((rawItem, index) => {
        let parsedData = {};

        if (rawItem?.data && typeof rawItem.data === "object") {
          parsedData = rawItem.data;
        }

        if (rawItem?.data && typeof rawItem.data === "string") {
          try {
            parsedData = JSON.parse(rawItem.data);
          } catch {
            parsedData = {};
          }
        }

        const item = {
          ...rawItem,
          ...parsedData,
        };

        return {
          id: item.id ?? `history-${index}`,

          date: item.date != null ? String(item.date).trim() : "",

          title: typeof item.title === "string" ? item.title.trim() : "",

          description:
            typeof item.description === "string" ? item.description.trim() : "",

          imageUrl: getImageUrl(item.image),
        };
      })
      .filter(
        (item) => item.date || item.title || item.description || item.imageUrl,
      )
      .sort((a, b) => getYear(a.date) - getYear(b.date));
  }, [historyData, getImageUrl]);

  const handleTimelineArrow = useCallback((direction) => {
    const timeline = timelineRef.current;

    if (!timeline) {
      return;
    }

    const STEP_DISTANCE = 432;

    timeline.scrollBy({
      left: direction === "next" ? STEP_DISTANCE : -STEP_DISTANCE,
      behavior: "smooth",
    });
  }, []);

  const handleTimelinePointerDown = useCallback((event) => {
    if (
      window.innerWidth < 992 ||
      event.pointerType !== "mouse" ||
      event.button !== 0
    ) {
      return;
    }

    const timeline = timelineRef.current;

    if (!timeline) {
      return;
    }

    timelineDragRef.current = {
      isDragging: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: timeline.scrollLeft,
    };

    setIsTimelineDragging(true);

    timeline.setPointerCapture?.(event.pointerId);

    event.preventDefault();
  }, []);

  const handleTimelinePointerMove = useCallback((event) => {
    const timeline = timelineRef.current;
    const dragState = timelineDragRef.current;

    if (!timeline || !dragState.isDragging) {
      return;
    }

    const dragDistance = event.clientX - dragState.startX;

    timeline.scrollLeft = dragState.startScrollLeft - dragDistance;
  }, []);

  const stopTimelineDragging = useCallback(() => {
    const timeline = timelineRef.current;
    const dragState = timelineDragRef.current;

    if (!dragState.isDragging) {
      return;
    }

    timelineDragRef.current.isDragging = false;

    setIsTimelineDragging(false);

    if (timeline && dragState.pointerId != null) {
      try {
        timeline.releasePointerCapture?.(dragState.pointerId);
      } catch {
        // Pointer daha önce bırakılmış olabilir.
      }
    }
  }, []);

  /* =========================================================
     SCROLL INTO VIEW
  ========================================================= */

  useEffect(() => {
    const timeline = timelineRef.current;

    if (!timeline) {
      return;
    }

    const steps = timeline.querySelectorAll(".demirsan-history-step");

    if (!steps.length) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");

            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: "0px 0px -8% 0px",
      },
    );

    steps.forEach((step, index) => {
      step.style.setProperty("--step-delay", `${Math.min(index * 45, 200)}ms`);

      observer.observe(step);
    });

    return () => {
      observer.disconnect();
    };
  }, [timelineItems]);

  /* =========================================================
     ÇİZGİ SCROLL İLE DOLSUN
  ========================================================= */

  useEffect(() => {
    if (!timelineItems.length) {
      return;
    }

    const timeline = timelineRef.current;

    if (!timeline) {
      return;
    }

    let animationFrame = null;

    const updateProgress = () => {
      /*
       * TELEFON + TABLET
       * aşağı doğru scroll
       */
      if (window.innerWidth <= 991) {
        const rect = timeline.getBoundingClientRect();

        const triggerPoint = window.innerHeight * 0.52;

        const travelled = triggerPoint - rect.top;

        const totalHeight = rect.height || 1;

        let progress = travelled / totalHeight;

        progress = Math.max(0, Math.min(1, progress));

        setTimelineProgress(progress);

        return;
      }

      /*
       * MASAÜSTÜ
       * sağa doğru scroll
       */
      const maxScroll = timeline.scrollWidth - timeline.clientWidth;

      if (maxScroll <= 0) {
        setTimelineProgress(0);
        return;
      }

      const progress = timeline.scrollLeft / maxScroll;

      setTimelineProgress(Math.max(0, Math.min(1, progress)));
    };

    const requestUpdate = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      animationFrame = requestAnimationFrame(updateProgress);
    };

    updateProgress();

    timeline.addEventListener("scroll", requestUpdate, { passive: true });

    window.addEventListener("scroll", requestUpdate, { passive: true });

    window.addEventListener("resize", requestUpdate);

    return () => {
      timeline.removeEventListener("scroll", requestUpdate);

      window.removeEventListener("scroll", requestUpdate);

      window.removeEventListener("resize", requestUpdate);

      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [timelineItems]);

  if (loading) {
    return null;
  }

  if (!timelineItems.length) {
    return null;
  }

  return (
    <>
      <style>
        {`

/* =========================================================
   HISTORY
========================================================= */

.demirsan-history {
  position: relative;
  width: 100%;

  padding: 30px 0;

  overflow: hidden;

  background:
    linear-gradient(
      180deg,
      #ffffff 0%,
      #f8f9fa 100%
    );
}


/* =========================================================
   HEADER
========================================================= */

.demirsan-history-header {
  position: relative;

  z-index: 5;

  max-width: 720px;

  margin:
    0 auto
    60px;

  padding:
    0 20px;

  text-align: center;
}

.demirsan-history-subtitle {
  margin:
    0 0
    8px;

  color: #df0a0a;

  font-size: 17px;
  font-weight: 600;

  line-height: 1.5;
  letter-spacing: 1.5px;

  text-transform: uppercase;
}

.demirsan-history-title {
  margin:
    0 0
    13px;

  color: #1c2539;

  font-size: 38px;
  font-weight: 700;

  line-height: 1.2;
}

.demirsan-history-description {
  max-width: 600px;

  margin: 0 auto;

  color: #687083;

  font-size: 14px;

  line-height: 1.7;
}


/* =========================================================
   TIMELINE
========================================================= */

.demirsan-vertical-timeline {
  position: relative;

  width: 100%;

  max-width: 1180px;

  margin: 0 auto;

  padding:
    20px 35px
    30px;
}


/* =========================================================
   ANA ÇİZGİ
========================================================= */

.demirsan-timeline-line {
  position: absolute;

  top: 0;
  bottom: 0;

  left: 50%;

  width: 5px;

  background: #e2e5e9;

  border-radius: 999px;

  transform:
    translateX(-50%);

  overflow: hidden;

  z-index: 1;
}


/* =========================================================
   KIRMIZI İLERLEME
========================================================= */

.demirsan-timeline-line-progress {
  position: absolute;

  top: 0;
  left: 0;

  width: 100%;

  background:
    linear-gradient(
      180deg,
      #bd0606 0%,
      #df0a0a 45%,
      #f32020 100%
    );

  border-radius: 999px;

  box-shadow:
    0 0 10px
    rgba(
      223,
      10,
      10,
      0.25
    );

  transition:
    height
    0.08s linear;
}


/* =========================================================
   STEP
========================================================= */

.demirsan-history-step {
  position: relative;

  display: grid;

  grid-template-columns:
    minmax(0, 1fr)
    80px
    minmax(0, 1fr);

  align-items: center;

  width: 100%;

  min-height: 310px;

  padding:
    35px 0;

  z-index: 2;
}


/* =========================================================
   AXIS
========================================================= */

.demirsan-step-axis {
  position: relative;

  grid-column: 2;
  grid-row: 1;

  display: flex;

  align-items: center;
  justify-content: center;

  align-self: center;

  width: 100%;

  z-index: 10;
}


/* =========================================================
   NOKTA
========================================================= */

.demirsan-step-dot {
  position: relative;

  display: block;

  width: 21px;
  height: 21px;

  background: #ffffff;

  border:
    5px solid
    #df0a0a;

  border-radius: 50%;

  box-shadow:
    0 0 0 7px
    rgba(
      223,
      10,
      10,
      0.09
    );

  transform:
    scale(0.75);

  transition:
    background
      0.4s ease,
    transform
      0.4s ease,
    box-shadow
      0.4s ease;
}

.demirsan-step-dot::after {
  content: "";

  position: absolute;

  top: 50%;
  left: 50%;

  width: 6px;
  height: 6px;

  background: #df0a0a;

  border-radius: 50%;

  transform:
    translate(
      -50%,
      -50%
    );
}

.demirsan-history-step.is-visible
.demirsan-step-dot {
  background: #df0a0a;

  transform:
    scale(1);

  box-shadow:
    0 0 0 8px
    rgba(
      223,
      10,
      10,
      0.12
    );
}

.demirsan-history-step.is-visible
.demirsan-step-dot::after {
  background: #ffffff;
}


/* =========================================================
   CONTENT
========================================================= */

.demirsan-step-content {
  position: relative;

  width: 100%;

  max-width: 430px;

  padding:
    23px 25px;

  background: #ffffff;

  border:
    1px solid
    #e4e7eb;

  border-radius: 14px;

  box-shadow:
    0 12px 32px
    rgba(
      19,
      28,
      43,
      0.065
    );

  opacity: 0;

  transform:
    translateX(-40px);

  transition:
    opacity
      0.65s ease
      var(--step-delay),
    transform
      0.65s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      var(--step-delay);

  justify-self: end;
}


/* =========================================================
   YEAR
========================================================= */

.demirsan-step-year {
  display: inline-flex;

  align-items: center;

  margin-bottom: 8px;

  color: #df0a0a;

  font-size: 17px;
  font-weight: 800;

  line-height: 1;
}

.demirsan-step-year::before {
  content: "";

  width: 20px;
  height: 3px;

  margin-right: 8px;

  background: #df0a0a;

  border-radius: 10px;
}


/* =========================================================
   TITLE
========================================================= */

.demirsan-step-title {
  margin:
    0 0
    7px;

  color: #1c2539;

  font-size: 22px;
  font-weight: 700;

  line-height: 1.3;
}


/* =========================================================
   DESCRIPTION
========================================================= */

.demirsan-step-description {
  margin: 0;

  color: #687083;

  font-size: 13px;

  line-height: 1.65;

  white-space: normal;
}


/* =========================================================
   IMAGE
========================================================= */

.demirsan-step-media {
  position: relative;

  display: flex;

  align-items: center;
  justify-content: center;

  width: 100%;

  max-width: 430px;

  padding: 10px;

  background: #ffffff;

  border:
    1px solid
    #e4e7eb;

  border-radius: 14px;

  box-shadow:
    0 14px 35px
    rgba(
      19,
      28,
      43,
      0.075
    );

  opacity: 0;

  transform:
    translateX(40px)
    scale(0.97);

  transition:
    opacity
      0.7s ease
      var(--step-delay),
    transform
      0.7s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      )
      var(--step-delay);

  justify-self: start;
}


/* Görsel kırpılmaz */

.demirsan-step-media img {
  display: block;

  width: auto;
  height: auto;

  max-width: 100%;

  object-fit: contain;

  object-position: center;

  border-radius: 9px;
}


/* =========================================================
   NORMAL
   SOL TEXT / SAĞ IMAGE
========================================================= */

.demirsan-history-step:not(.is-reverse)
.demirsan-step-content {
  grid-column: 1;

  justify-self: end;
}

.demirsan-history-step:not(.is-reverse)
.demirsan-step-media {
  grid-column: 3;

  justify-self: start;
}


/* =========================================================
   REVERSE
   SOL IMAGE / SAĞ TEXT
========================================================= */

.demirsan-history-step.is-reverse
.demirsan-step-media {
  grid-column: 1;

  justify-self: end;

  transform:
    translateX(-40px)
    scale(0.97);
}

.demirsan-history-step.is-reverse
.demirsan-step-content {
  grid-column: 3;

  justify-self: start;

  transform:
    translateX(40px);
}


/* =========================================================
   VISIBLE
========================================================= */

.demirsan-history-step.is-visible
.demirsan-step-content {
  opacity: 1;

  transform:
    translateX(0);
}

.demirsan-history-step.is-visible
.demirsan-step-media {
  opacity: 1;

  transform:
    translateX(0)
    scale(1);
}


/* =========================================================
   GÖRSELSİZ
========================================================= */

.demirsan-history-step.no-image
.demirsan-step-content {
  max-width: 460px;
}


/* =========================================================
   TABLET
========================================================= */

@media (max-width: 991px) {

  .demirsan-history-title {
    font-size: 33px;
  }

  .demirsan-vertical-timeline {
    padding:
      15px 25px
      25px;
  }

  .demirsan-history-step {
    grid-template-columns:
      minmax(0, 1fr)
      65px
      minmax(0, 1fr);

    min-height: 280px;

    padding:
      30px 0;
  }

  .demirsan-step-content {
    padding:
      19px 20px;
  }

  .demirsan-step-title {
    font-size: 19px;
  }

  .demirsan-step-description {
    font-size: 12.5px;
  }

  .demirsan-step-year {
    font-size: 16px;
  }

}



/* =========================================================
   REDUCED MOTION
========================================================= */

@media (
  prefers-reduced-motion:
  reduce
) {

  .demirsan-step-content,
  .demirsan-step-media,
  .demirsan-step-dot,
  .demirsan-timeline-line-progress {
    transition:
      none !important;
  }

  .demirsan-step-content,
  .demirsan-step-media {
    opacity: 1;

    transform:
      none !important;
  }

}
/* =========================================================
   NORMAL
   TEXT SOL / NOKTA ORTA / IMAGE SAĞ
========================================================= */

.demirsan-history-step:not(.is-reverse)
.demirsan-step-content {
  grid-column: 1;
  grid-row: 1;

  justify-self: end;
  align-self: center;
}

.demirsan-history-step:not(.is-reverse)
.demirsan-step-axis {
  grid-column: 2;
  grid-row: 1;

  align-self: center;
}

.demirsan-history-step:not(.is-reverse)
.demirsan-step-media {
  grid-column: 3;
  grid-row: 1;

  justify-self: start;
  align-self: center;
}


/* =========================================================
   REVERSE
   IMAGE SOL / NOKTA ORTA / TEXT SAĞ
========================================================= */

.demirsan-history-step.is-reverse
.demirsan-step-media {
  grid-column: 1;
  grid-row: 1;

  justify-self: end;
  align-self: center;

  transform:
    translateX(-40px)
    scale(0.97);
}

.demirsan-history-step.is-reverse
.demirsan-step-axis {
  grid-column: 2;
  grid-row: 1;

  align-self: center;
}

.demirsan-history-step.is-reverse
.demirsan-step-content {
  grid-column: 3;
  grid-row: 1;

  justify-self: start;
  align-self: center;

  transform:
    translateX(40px);
}

/* =========================================================
   DESKTOP - YATAY TIMELINE
========================================================= */

@media (min-width: 992px) {

  .demirsan-vertical-timeline {
    position: relative !important;

    display: flex !important;
    align-items: stretch !important;

    gap: 34px !important;

    width: 100% !important;
    max-width: 1180px !important;

    margin: 0 auto !important;

    padding: 35px 35px 50px !important;

    overflow-x: auto !important;
    overflow-y: hidden !important;

    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;

    box-sizing: border-box !important;
  }

  .demirsan-vertical-timeline::-webkit-scrollbar {
    height: 10px;
  }

  .demirsan-vertical-timeline::-webkit-scrollbar-track {
    background: #eceff2;
    border-radius: 999px;
  }

  .demirsan-vertical-timeline::-webkit-scrollbar-thumb {
    background: #df0a0a;
    border-radius: 999px;
  }

  .demirsan-timeline-line {
    position: absolute !important;

    top: 50% !important;
    bottom: auto !important;

    left: 35px !important;
    right: 35px !important;

    width: auto !important;
    height: 5px !important;

    transform: translateY(-50%) !important;

    background: #e2e5e9 !important;
    border-radius: 999px !important;

    overflow: hidden !important;
    z-index: 1 !important;
  }

  .demirsan-timeline-line-progress {
    position: absolute !important;

    top: 0 !important;
    left: 0 !important;

    width: 0;
    height: 100% !important;

    background: linear-gradient(
      90deg,
      #bd0606 0%,
      #df0a0a 45%,
      #f32020 100%
    ) !important;

    border-radius: 999px !important;

    transition: width 0.08s linear !important;
  }

  .demirsan-history-step,
  .demirsan-history-step.is-reverse,
  .demirsan-history-step:not(.is-reverse) {
    position: relative !important;

    flex: 0 0 360px !important;
    width: 360px !important;
    min-width: 360px !important;
    min-height: 0 !important;

    display: grid !important;
    grid-template-columns: 1fr !important;
    grid-template-rows: auto 90px auto !important;

    align-items: center !important;

    padding: 0 !important;
    margin: 0 !important;

    box-sizing: border-box !important;

    scroll-snap-align: start;

    z-index: 2 !important;
  }

  .demirsan-step-content,
  .demirsan-history-step.is-reverse .demirsan-step-content,
  .demirsan-history-step:not(.is-reverse) .demirsan-step-content {
    grid-column: 1 !important;
    grid-row: 1 !important;

    width: 100% !important;
    max-width: none !important;

    justify-self: stretch !important;
    align-self: end !important;

    margin: 0 !important;

    opacity: 0;
    transform: translateY(-26px) !important;
  }

  .demirsan-step-axis,
  .demirsan-history-step.is-reverse .demirsan-step-axis,
  .demirsan-history-step:not(.is-reverse) .demirsan-step-axis {
    grid-column: 1 !important;
    grid-row: 2 !important;

    width: 100% !important;
    height: 100% !important;

    display: flex !important;
    align-items: center !important;
    justify-content: center !important;

    position: relative !important;

    transform: none !important;
  }

  .demirsan-step-media,
  .demirsan-history-step.is-reverse .demirsan-step-media,
  .demirsan-history-step:not(.is-reverse) .demirsan-step-media {
    grid-column: 1 !important;
    grid-row: 3 !important;

    width: 100% !important;
    max-width: none !important;

    justify-self: stretch !important;
    align-self: start !important;

    margin: 0 !important;

    opacity: 0;
    transform: translateY(26px) scale(0.97) !important;
  }

  .demirsan-history-step.is-visible .demirsan-step-content {
    opacity: 1 !important;
    transform: translateY(0) !important;
  }

  .demirsan-history-step.is-visible .demirsan-step-media {
    opacity: 1 !important;
    transform: translateY(0) scale(1) !important;
  }

  .demirsan-step-dot {
    transform: scale(0.8);
  }

  .demirsan-history-step.is-visible .demirsan-step-dot {
    transform: scale(1);
  }
}

/* =========================================================
   MOBİL TIMELINE - FINAL
========================================================= */

@media (max-width: 767px) {

  .demirsan-history {
    padding: 50px 0 70px !important;
    overflow: hidden !important;
  }

  /* HEADER */

  .demirsan-history-header {
    margin: 0 0 35px !important;
    padding: 0 16px !important;
    text-align: left !important;
  }

  .demirsan-history-subtitle {
    margin: 0 0 5px !important;

    font-size: 12px !important;
    letter-spacing: 1px !important;
  }

  .demirsan-history-title {
    margin: 0 0 8px !important;

    font-size: 25px !important;
    line-height: 1.25 !important;
  }

  .demirsan-history-description {
    margin: 0 !important;

    font-size: 12px !important;
    line-height: 1.6 !important;
  }


  /* =======================================================
     TIMELINE WRAPPER

     Çizgi ve noktalar TEK koordinat kullanıyor.
  ======================================================= */

  .demirsan-vertical-timeline {
    --axis-x: 30px;
    --line-width: 4px;
    --card-left: 64px;

    position: relative !important;

    display: flex !important;
    flex-direction: column !important;

    /*
     * KAYITLAR ARASINA BOŞLUK
     */
    gap: 36px !important;

    width: 100% !important;
    max-width: 100% !important;

    margin: 0 !important;

    padding:
      10px 14px
      20px 0 !important;

    box-sizing: border-box !important;
  }


  /* =======================================================
     DİKEY ÇİZGİ

     Çizginin merkezi tam --axis-x.
  ======================================================= */

  .demirsan-timeline-line {
    position: absolute !important;

    top: 0 !important;
    bottom: 0 !important;

    /*
     * left çizginin sol kenarı.
     * Merkez = --axis-x
     */
    left:
      calc(
        var(--axis-x) -
        (var(--line-width) / 2)
      ) !important;

    width: var(--line-width) !important;
    height: auto !important;

    margin: 0 !important;

    background: #e1e4e8 !important;

    border-radius: 999px !important;

    /*
     * ÖNEMLİ:
     * translate artık YOK.
     */
    transform: none !important;

    overflow: hidden !important;

    z-index: 1 !important;
  }


  /* KIRMIZI DOLAN ÇİZGİ */

  .demirsan-timeline-line-progress {
    position: absolute !important;

    top: 0 !important;
    left: 0 !important;

    width: 100% !important;

    background:
      linear-gradient(
        180deg,
        #bd0606 0%,
        #df0a0a 50%,
        #f32020 100%
      ) !important;

    border-radius: 999px !important;

    box-shadow:
      0 0 8px
      rgba(
        223,
        10,
        10,
        0.22
      ) !important;
  }


  /* =======================================================
     HER BİR KAYIT
  ======================================================= */

  .demirsan-history-step,
  .demirsan-history-step.is-reverse,
  .demirsan-history-step:not(.is-reverse) {
    position: relative !important;

    display: flex !important;

    flex-direction: column !important;

    align-items: stretch !important;
    justify-content: flex-start !important;

    width: 100% !important;

    min-width: 0 !important;
    min-height: 0 !important;

    /*
     * Kendi içinde ekstra dev boşluk yok.
     * Asıl kayıt aralığı wrapper gap:36px.
     */
    padding:
      18px 0
      18px var(--card-left) !important;

    margin: 0 !important;

    box-sizing: border-box !important;

    grid-template-columns: none !important;
    grid-template-rows: none !important;
  }


  /* =======================================================
     NOKTA AXIS

     NOKTA MERKEZİ = --axis-x
  ======================================================= */

  .demirsan-step-axis,
  .demirsan-history-step.is-reverse
  .demirsan-step-axis,
  .demirsan-history-step:not(.is-reverse)
  .demirsan-step-axis {
    position: absolute !important;

    /*
     * Yıl satırına yakın.
     */
    top: 38px !important;

    left: var(--axis-x) !important;

    display: block !important;

    width: 0 !important;
    height: 0 !important;

    margin: 0 !important;
    padding: 0 !important;

    grid-column: auto !important;
    grid-row: auto !important;

    align-self: auto !important;
    justify-self: auto !important;

    transform: none !important;

    z-index: 20 !important;
  }


  /* =======================================================
     YUVARLAK
  ======================================================= */

  .demirsan-step-dot {
    position: absolute !important;

    top: 0 !important;
    left: 0 !important;

    display: block !important;

    width: 18px !important;
    height: 18px !important;

    margin: 0 !important;

    background: #ffffff !important;

    border:
      4px solid
      #df0a0a !important;

    border-radius: 50% !important;

    box-shadow:
      0 0 0 5px
      rgba(
        223,
        10,
        10,
        0.10
      ) !important;

    /*
     * Yuvarlağın merkezi
     * tam axis'in üzerine gelir.
     */
    transform:
      translate(-50%, -50%)
      scale(0.82) !important;

    transform-origin: center !important;

    z-index: 20 !important;
  }


  .demirsan-step-dot::after {
    content: "" !important;

    position: absolute !important;

    top: 50% !important;
    left: 50% !important;

    width: 5px !important;
    height: 5px !important;

    background: #df0a0a !important;

    border-radius: 50% !important;

    transform:
      translate(-50%, -50%) !important;
  }


  .demirsan-history-step.is-visible
  .demirsan-step-dot {
    background: #df0a0a !important;

    transform:
      translate(-50%, -50%)
      scale(1) !important;

    box-shadow:
      0 0 0 6px
      rgba(
        223,
        10,
        10,
        0.12
      ) !important;
  }


  .demirsan-history-step.is-visible
  .demirsan-step-dot::after {
    background: #ffffff !important;
  }


  /* =======================================================
     METİN KARTI
     Mobilde hepsi aynı hizada
  ======================================================= */

  .demirsan-step-content,
  .demirsan-history-step.is-reverse
  .demirsan-step-content,
  .demirsan-history-step:not(.is-reverse)
  .demirsan-step-content {
    order: 1 !important;

    position: relative !important;

    display: block !important;

    width: 100% !important;
    max-width: none !important;

    min-width: 0 !important;

    margin: 0 !important;

    padding:
      13px 14px !important;

    box-sizing: border-box !important;

    background: #ffffff !important;

    border:
      1px solid
      #e4e7eb !important;

    border-radius: 10px !important;

    box-shadow:
      0 7px 20px
      rgba(
        19,
        28,
        43,
        0.06
      ) !important;

    grid-column: auto !important;
    grid-row: auto !important;

    justify-self: auto !important;
    align-self: stretch !important;

    opacity: 0 !important;

    transform:
      translateY(14px) !important;

    transition:
      opacity 0.5s ease,
      transform 0.5s ease !important;
  }


  /* =======================================================
     GÖRSEL
  ======================================================= */

  .demirsan-step-media,
  .demirsan-history-step.is-reverse
  .demirsan-step-media,
  .demirsan-history-step:not(.is-reverse)
  .demirsan-step-media {
    order: 2 !important;

    position: relative !important;

    display: flex !important;

    align-items: center !important;
    justify-content: center !important;

    width: 100% !important;
    max-width: none !important;

    min-width: 0 !important;

    margin:
      10px 0
      0 !important;

    padding: 6px !important;

    box-sizing: border-box !important;

    background: #ffffff !important;

    border:
      1px solid
      #e4e7eb !important;

    border-radius: 10px !important;

    box-shadow:
      0 7px 20px
      rgba(
        19,
        28,
        43,
        0.06
      ) !important;

    grid-column: auto !important;
    grid-row: auto !important;

    justify-self: auto !important;
    align-self: stretch !important;

    opacity: 0 !important;

    transform:
      translateY(14px)
      scale(0.99) !important;

    transition:
      opacity 0.5s ease,
      transform 0.5s ease !important;
  }


  /* =======================================================
     GÖRSEL KIRPILMASIN
  ======================================================= */

  .demirsan-step-media img {
    display: block !important;

    width: auto !important;
    height: auto !important;

    max-width: 100% !important;
    max-height: none !important;

    margin: 0 auto !important;

    object-fit: contain !important;
    object-position: center !important;

    border-radius: 7px !important;
  }


  /* =======================================================
     VISIBLE
  ======================================================= */

  .demirsan-history-step.is-visible
  .demirsan-step-content {
    opacity: 1 !important;

    transform:
      translateY(0) !important;
  }


  .demirsan-history-step.is-visible
  .demirsan-step-media {
    opacity: 1 !important;

    transform:
      translateY(0)
      scale(1) !important;
  }


  /* =======================================================
     FONTLAR
  ======================================================= */

  .demirsan-step-year {
    display: inline-flex !important;

    align-items: center !important;

    margin:
      0 0
      5px !important;

    color: #df0a0a !important;

    font-size: 11.5px !important;
    font-weight: 800 !important;

    line-height: 1 !important;
  }


  .demirsan-step-year::before {
    content: "" !important;

    width: 14px !important;
    height: 2px !important;

    margin-right: 6px !important;

    background: #df0a0a !important;

    border-radius: 10px !important;
  }


  .demirsan-step-title {
    margin:
      0 0
      4px !important;

    color: #1c2539 !important;

    font-size: 15px !important;
    font-weight: 700 !important;

    line-height: 1.3 !important;

    white-space: normal !important;
    overflow-wrap: break-word !important;
  }


  .demirsan-step-description {
    margin: 0 !important;

    color: #687083 !important;

    font-size: 11.5px !important;

    line-height: 1.5 !important;
  }
}


/* =========================================================
   KÜÇÜK TELEFON
========================================================= */

@media (max-width: 480px) {

  .demirsan-history-header {
    padding:
      0 14px !important;
  }

  .demirsan-history-title {
    font-size:
      23px !important;
  }

  .demirsan-vertical-timeline {
    --axis-x: 25px;
    --card-left: 56px;

    /*
     * Biraz daha kompakt ama
     * birbirine yapışık değil.
     */
    gap: 30px !important;

    padding-right:
      12px !important;
  }

  .demirsan-history-step,
  .demirsan-history-step.is-reverse,
  .demirsan-history-step:not(.is-reverse) {
    padding-top:
      17px !important;

    padding-bottom:
      17px !important;
  }

  .demirsan-step-axis,
  .demirsan-history-step.is-reverse
  .demirsan-step-axis,
  .demirsan-history-step:not(.is-reverse)
  .demirsan-step-axis {
    top:
      36px !important;
  }

  .demirsan-step-content {
    padding:
      12px 12px !important;
  }

  .demirsan-step-year {
    font-size:
      11px !important;
  }

  .demirsan-step-title {
    font-size:
      14px !important;
  }

  .demirsan-step-description {
    font-size:
      11px !important;
  }
}
  .demirsan-history-subtitle {
  font-size: 17px;
}
  .demirsan-step-year {
  font-size: 17px;
}
  .demirsan-step-title {
  font-size: 22px;
}
  @media (max-width: 991px) {
  .demirsan-step-title {
    font-size: 19px;
  }

  .demirsan-step-year {
    font-size: 16px;
  }
}
  /* =========================================================
   EVENT WRAPPER

   Mobilde wrapper yokmuş gibi davranır.
   Böylece mevcut mobil tasarım bozulmaz.
========================================================= */

.demirsan-step-card {
  display: contents;
}


/* =========================================================
   DESKTOP YATAY TIMELINE
========================================================= */

@media (min-width: 768px) {

  /*
   * Timeline'ın tamamı.
   * Daha geniş ve ferah bir alan.
   */
  .demirsan-history-timeline-shell {
    position: relative;

    width: 100%;
    max-width: 1450px;

    margin: 0 auto;

    padding:
      70px 32px
      20px;

    box-sizing: border-box;
  }


  /* =======================================================
     SAĞ ÜST OKLAR
  ======================================================= */

  .demirsan-history-navigation {
    position: absolute;

    top: 0;
    right: 32px;

    display: flex;

    gap: 10px;

    z-index: 50;
  }


.demirsan-history-nav-button {
  display: inline-flex;

  align-items: center;
  justify-content: center;

  gap: 8px;

  min-width: 112px;
  height: 46px;

  margin: 0;
  padding: 0 16px;

  color: #1c2539;
  background: #ffffff;

  border: 1px solid #d7dce2;
  border-radius: 12px;

  box-shadow:
    0 8px 24px
    rgba(19, 28, 43, 0.08);

  font-size: 13px;
  font-weight: 700;
  line-height: 1;

  cursor: pointer;

  transition:
    color 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}

.demirsan-history-nav-button svg {
  width: 18px;
  height: 18px;

  flex: 0 0 auto;
}

.demirsan-history-nav-button:hover {
  color: #ffffff;

  background: #df0a0a;

  border-color: #df0a0a;

  box-shadow:
    0 10px 26px
    rgba(223, 10, 10, 0.18);

  transform: translateY(-2px);
}

.demirsan-history-nav-button:focus-visible {
  outline: 3px solid rgba(223, 10, 10, 0.2);
  outline-offset: 3px;
}


  .demirsan-history-nav-button:hover {
    color: #ffffff;

    background: #df0a0a;

    border-color: #df0a0a;

    transform:
      translateY(-2px);
  }


  /* =======================================================
     YATAY SCROLL ALANI
  ======================================================= */

  .demirsan-vertical-timeline {
    --step-width: 360px;
    --step-gap: 72px;

    position: relative !important;

    display: flex !important;

    flex-direction: row !important;

    align-items: stretch !important;

    gap: var(--step-gap) !important;

    width: 100% !important;
    max-width: none !important;

    min-height: 950px !important;

    margin: 0 !important;

    padding:
      35px 56px
      35px !important;

    box-sizing: border-box !important;

    overflow-x: auto !important;
    overflow-y: hidden !important;

    scroll-behavior: smooth;

    scroll-snap-type:
      x mandatory;

    scrollbar-width: none;

    overscroll-behavior-x:
      contain;
      cursor: grab;
touch-action: pan-y;
  }


  .demirsan-vertical-timeline::-webkit-scrollbar {
    display: none;
  }


  /* =======================================================
     ORTADAKİ YATAY ZAMAN ÇİZGİSİ
  ======================================================= */

  .demirsan-timeline-line {
    position: absolute !important;

    /*
     * Timeline'ın tam ortası
     */
    top: 50% !important;

    /*
     * İlk kart:
     * padding 56
     * +
     * kartın yarısı 180
     */
    left: 236px !important;

    bottom: auto !important;

    width:
      var(
        --timeline-line-width
      ) !important;

    height: 4px !important;

    background:
      #dfe3e8 !important;

    border-radius:
      999px !important;

    transform:
      translateY(-50%) !important;

    overflow: hidden !important;

    z-index: 1 !important;
  }


  /* =======================================================
     KIRMIZI PROGRESS
  ======================================================= */

  .demirsan-timeline-line-progress {
    position: absolute !important;

    top: 0 !important;
    left: 0 !important;

    width:
      var(
        --timeline-progress
      ) !important;

    height: 100% !important;

    background:
      linear-gradient(
        90deg,
        #bd0606 0%,
        #df0a0a 50%,
        #f32020 100%
      ) !important;

    border-radius:
      999px !important;

    transition:
      width 0.12s linear !important;

    box-shadow:
      0 0 10px
      rgba(
        223,
        10,
        10,
        0.22
      );
  }


  /* =======================================================
     HER TARİH
  ======================================================= */

  .demirsan-history-step,
  .demirsan-history-step.is-reverse,
  .demirsan-history-step:not(.is-reverse) {
    position: relative !important;

    display: block !important;

    flex:
      0 0
      var(--step-width) !important;

    width:
      var(--step-width) !important;

    min-width:
      var(--step-width) !important;

    height: 900px !important;

    min-height:
      900px !important;

    margin: 0 !important;
    padding: 0 !important;

    grid-template-columns:
      none !important;

    scroll-snap-align:
      center;

    box-sizing:
      border-box !important;

    z-index: 5 !important;
  }


  /* =======================================================
     EVENT KARTI
  ======================================================= */

  .demirsan-step-card {
    position: absolute;

    left: 0;

    display: flex;

    flex-direction: column;

    gap: 12px;

    width: 100%;

    box-sizing: border-box;

    z-index: 6;
  }


  /*
   * 1, 3, 5...
   * ÇİZGİNİN ÜSTÜ
   */
  .demirsan-history-step:not(.is-reverse)
  .demirsan-step-card {
    bottom:
      calc(
        50% + 30px
      );
  }


  /*
   * 2, 4, 6...
   * ÇİZGİNİN ALTI
   */
  .demirsan-history-step.is-reverse
  .demirsan-step-card {
    top:
      calc(
        50% + 30px
      );
  }


  /* =======================================================
     NOKTA TAM ÇİZGİNİN ÜSTÜNDE
  ======================================================= */

  .demirsan-step-axis,
  .demirsan-history-step.is-reverse
  .demirsan-step-axis,
  .demirsan-history-step:not(.is-reverse)
  .demirsan-step-axis {
    position: absolute !important;

    top: 50% !important;
    left: 50% !important;

    display: flex !important;

    align-items: center !important;
    justify-content: center !important;

    width: 26px !important;
    height: 26px !important;

    margin: 0 !important;
    padding: 0 !important;

    grid-column:
      auto !important;

    grid-row:
      auto !important;

    transform:
      translate(
        -50%,
        -50%
      ) !important;

    z-index: 20 !important;
  }


  .demirsan-step-dot {
    position: relative !important;

    width: 20px !important;
    height: 20px !important;

    background:
      #ffffff !important;

    border:
      4px solid
      #df0a0a !important;

    border-radius:
      50% !important;

    box-shadow:
      0 0 0 7px
      rgba(
        223,
        10,
        10,
        0.10
      ) !important;

    transform:
      scale(0.88) !important;

    z-index: 20;
  }


  .demirsan-history-step.is-visible
  .demirsan-step-dot {
    background:
      #df0a0a !important;

    transform:
      scale(1) !important;
  }


  /* =======================================================
     NOKTADAN KARTA KISA BAĞLANTI ÇİZGİSİ
  ======================================================= */

  .demirsan-history-step::after {
    content: "";

    position: absolute;

    left: 50%;

    width: 2px;
    height: 26px;

    background:
      #d7dce2;

    transform:
      translateX(-50%);

    z-index: 2;
  }


  /*
   * Üstteki karta bağlanır
   */
  .demirsan-history-step:not(.is-reverse)::after {
    top:
      calc(
        50% - 26px
      );
  }


  /*
   * Alttaki karta bağlanır
   */
  .demirsan-history-step.is-reverse::after {
    top: 50%;
  }


  /* =======================================================
     TEXT KART
  ======================================================= */

  .demirsan-step-content,
  .demirsan-history-step.is-reverse
  .demirsan-step-content,
  .demirsan-history-step:not(.is-reverse)
  .demirsan-step-content {
    position: relative !important;

    display: block !important;

    width: 100% !important;
    max-width: none !important;

    margin: 0 !important;

    padding:
      20px 22px !important;

    box-sizing:
      border-box !important;

    background:
      #ffffff !important;

    border:
      1px solid
      #e4e7eb !important;

    border-radius:
      14px !important;

    box-shadow:
      0 12px 32px
      rgba(
        19,
        28,
        43,
        0.065
      ) !important;

    grid-column:
      auto !important;

    grid-row:
      auto !important;

    justify-self:
      auto !important;

    align-self:
      auto !important;

    opacity: 0;

    transform:
      translateY(15px) !important;

    transition:
      opacity 0.5s ease,
      transform 0.5s ease !important;
  }


  /* =======================================================
     IMAGE
  ======================================================= */

  .demirsan-step-media,
  .demirsan-history-step.is-reverse
  .demirsan-step-media,
  .demirsan-history-step:not(.is-reverse)
  .demirsan-step-media {
    position: relative !important;

    display: flex !important;

    align-items:
      center !important;

    justify-content:
      center !important;

    width: 100% !important;
    max-width: none !important;

    height: 240px !important;

    margin: 0 !important;

    padding: 7px !important;

    box-sizing:
      border-box !important;

    background:
      #ffffff !important;

    border:
      1px solid
      #e4e7eb !important;

    border-radius:
      14px !important;

    box-shadow:
      0 12px 30px
      rgba(
        19,
        28,
        43,
        0.06
      ) !important;

    grid-column:
      auto !important;

    grid-row:
      auto !important;

    justify-self:
      auto !important;

    align-self:
      auto !important;

    opacity: 0;

    transform:
      translateY(15px)
      scale(0.98) !important;
  }


  .demirsan-step-media img {
    display: block !important;

    width: auto !important;
    height: auto !important;

    max-width:
      100% !important;

    max-height:
      100% !important;

    object-fit:
      contain !important;

    object-position:
      center !important;

    border-radius:
      9px !important;
  }


  /* =======================================================
     ANIMATION
  ======================================================= */

  .demirsan-history-step.is-visible
  .demirsan-step-content {
    opacity: 1 !important;

    transform:
      translateY(0) !important;
  }


  .demirsan-history-step.is-visible
  .demirsan-step-media {
    opacity: 1 !important;

    transform:
      translateY(0)
      scale(1) !important;
  }


  /* =======================================================
     TYPOGRAPHY
  ======================================================= */

  .demirsan-step-year {
    font-size:
      17px !important;
  }


  .demirsan-step-title {
    font-size:
      21px !important;
  }


  .demirsan-step-description {
    font-size:
      13px !important;
  }

}
  .demirsan-history-navigation {
  display: none;
}

@media (min-width: 992px) {
  .demirsan-history-navigation {
    display: flex;
  }
}
  .demirsan-vertical-timeline.is-dragging {
  cursor: grabbing;

  scroll-snap-type: none;
  scroll-behavior: auto;

  user-select: none;
}

.demirsan-vertical-timeline.is-dragging img {
  pointer-events: none;
  -webkit-user-drag: none;
}
        `}
      </style>

      <section className="demirsan-history">
        {/* HEADER */}
        <div className="container">
          <div className="demirsan-history-header">
            <p className="demirsan-history-subtitle">GEÇMİŞTEN GELECEĞE</p>

            <h2 className="demirsan-history-title">Demirsan'ın Yolculuğu</h2>

            <p className="demirsan-history-description">
              Kuruluşumuzdan bugüne uzanan yolculuğumuzdaki önemli dönüm
              noktalarını keşfedin.
            </p>
          </div>
        </div>

        {/* TIMELINE */}
        {/* TIMELINE */}
        <div className="demirsan-history-timeline-shell">
          {/* DESKTOP OKLAR */}
          <div className="demirsan-history-navigation">
            <button
              type="button"
              className="demirsan-history-nav-button"
              onClick={() => handleTimelineArrow("prev")}
              aria-label="Önceki tarih"
              title="Önceki tarih"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M15 18l-6-6 6-6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <span>Önceki</span>
            </button>

            <button
              type="button"
              className="demirsan-history-nav-button"
              onClick={() => handleTimelineArrow("next")}
              aria-label="Sonraki tarih"
              title="Sonraki tarih"
            >
              <span>Sonraki</span>

              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M9 6l6 6-6 6"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div
            ref={timelineRef}
            className={`demirsan-vertical-timeline${
              isTimelineDragging ? " is-dragging" : ""
            }`}
            onPointerDown={handleTimelinePointerDown}
            onPointerMove={handleTimelinePointerMove}
            onPointerUp={stopTimelineDragging}
            onPointerCancel={stopTimelineDragging}
            onDragStart={(event) => event.preventDefault()}
            style={{
              "--timeline-progress": `${timelineProgress * 100}%`,

              "--timeline-line-width": `${
                Math.max(0, timelineItems.length - 1) * 432
              }px`,
            }}
          >
            {/* ANA ÇİZGİ */}
            <div className="demirsan-timeline-line">
              <div
                className="demirsan-timeline-line-progress"
                style={
                  isDesktop
                    ? {
                        width: `${timelineProgress * 100}%`,
                        height: "100%",
                      }
                    : {
                        height: `${timelineProgress * 100}%`,
                        width: "100%",
                      }
                }
              />
            </div>

            {timelineItems.map((item, index) => {
              const isReverse = index % 2 !== 0;

              return (
                <article
                  key={item.id}
                  className={[
                    "demirsan-history-step",

                    isReverse ? "is-reverse" : "",

                    !item.imageUrl ? "no-image" : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {/* EVENT KARTI */}
                  <div className="demirsan-step-card">
                    {/* METİN */}
                    <div className="demirsan-step-content">
                      {item.date && (
                        <span className="demirsan-step-year">{item.date}</span>
                      )}

                      {item.title && (
                        <h3 className="demirsan-step-title">{item.title}</h3>
                      )}

                      {item.description && (
                        <p className="demirsan-step-description">
                          {item.description}
                        </p>
                      )}
                    </div>

                    {/* GÖRSEL */}
                    {item.imageUrl && (
                      <div className="demirsan-step-media">
                        <img
                          src={item.imageUrl}
                          alt={item.title || "Demirsan tarihçe"}
                          loading="lazy"
                          onError={(event) => {
                            console.error(
                              "TIMELINE_IMAGE_ERROR:",
                              event.currentTarget.src,
                            );

                            event.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* NOKTA */}
                  <div className="demirsan-step-axis">
                    <span className="demirsan-step-dot" />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

export default AboutHistory;
